import {
  Box,
  Button,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { Delete, Cancel, CheckCircle } from '@mui/icons-material';
import * as React from 'react';
import { useState, useCallback, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { VehicleFile, VEHICLE_FIELDS } from 'types/vehicles/types';
import { vehiclePageStyles } from '../styles';
import {
  FILE_TYPE_GATE_PASS,
  FILE_TYPE_BILL_OF_SALE,
  FILE_TYPE_POA,
  FILE_TYPE_INVOICE,
  FILE_TYPE_TITLE,
  FILE_TYPE_OTHER,
} from '../../config';
import axiosService from 'utils/axios';
import { useFileUpload } from '../hooks/use_file_upload';
import { documentDisplayName } from './documentDisplayName';
import { ErrorResponse } from 'types/general/types';
import '../styles.css';

export interface VehicleDocumentsSectionData {
  title_status: string;
}

interface TemporaryVehicleFileInterface {
  fileType: string;
  files: File[];
}

interface FileToDeleteInterface {
  fileType: string;
  fileId: string;
}

interface VehicleDocumentsSectionProps {
  vehicleId?: string;
  initialFiles?: VehicleFile[];
  onError?: (error: ErrorResponse | null) => void;
  setIsLoading?: (loading: boolean) => void;
  updateSubmitData: (data: VehicleDocumentsSectionData) => void;
  // POA-related props
  importerChoice: { id: string; name: string } | null;
  onPOAUploaded?: () => void;
  getPOAFile?: () => string | undefined;
  canEdit: (fieldName: string) => boolean;
  isSuperuser: boolean;
}

export interface VehicleDocumentsSectionRef {
  uploadNewVehicleFiles: (vehicleId: string) => Promise<void>;
  getTemporaryFiles: () => TemporaryVehicleFileInterface[];
  updateSectionData: (vehicleData: { title_status?: string }) => void;
}

const VehicleDocumentsSection = React.forwardRef(
  (
    {
      vehicleId,
      initialFiles = [],
      onError,
      setIsLoading,
      updateSubmitData,
      importerChoice,
      onPOAUploaded,
      getPOAFile,
      canEdit,
      isSuperuser,
    }: VehicleDocumentsSectionProps,
    ref
  ) => {
    const { id } = useParams();
    const addingNewVehicle = id === 'new';

    // File upload hook
    const { uploadFiles } = useFileUpload();

    // Title status state
    const [titleStatusInput, setTitleStatusInput] = useState('');

    // Files state
    const [newVehicleTemporaryFiles, setNewVehicleTemporaryFiles] = useState<TemporaryVehicleFileInterface[]>([]);
    const [vehicleFilesToDisplay, setVehicleFilesToDisplay] = useState<VehicleFile[]>([]);
    const [fileToDelete, setFileToDelete] = useState<FileToDeleteInterface | null>(null);

    const parseVehicleFiles = useCallback((files: VehicleFile[]) => {
      return files.map((vehicleFile: VehicleFile) => ({
        ...vehicleFile,
        file: `${process.env.REACT_APP_MEDIA_API_URL}/${vehicleFile.file}`,
      }));
    }, []);

    const updateVehicleFiles = useCallback(
      (id: string) => {
        if (id && !addingNewVehicle) {
          setIsLoading?.(true);
          axiosService
            .get(`/vehicles/vehicle/${id}/`)
            .then(res => {
              setVehicleFilesToDisplay(parseVehicleFiles(res.data.files));
            })
            .catch(() => onError?.({ status: '', statusText: 'Error', message: 'Failed to fetch vehicle files.' }))
            .finally(() => setIsLoading?.(false));
        }
      },
      [addingNewVehicle, parseVehicleFiles, onError, setIsLoading]
    );

    const uploadVehicleFiles = useCallback(
      (vehicleIdToUpload: string, fileType: string, files: File[]) => {
        setIsLoading?.(true);
        return uploadFiles(
          vehicleIdToUpload,
          fileType,
          files,
          () => {
            // On success
            onError?.(null);
            updateVehicleFiles(vehicleIdToUpload);
          },
          error => {
            // On error
            onError?.(error);
          },
          false
        ).finally(() => {
          setIsLoading?.(false);
        });
      },
      [uploadFiles, updateVehicleFiles, onError, setIsLoading]
    );

    const onVehicleFileInput = async (fileType: string, e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        // Cast the files to an array
        const filesArr = [...files];

        // Clear input value in order to be able to upload again
        e.target.value = '';

        if (vehicleId) {
          // Existing entry, upload the submitted files directly
          uploadVehicleFiles(vehicleId, fileType, filesArr);
        } else {
          // New entry, do not upload the files yet, save them in order to upload them later
          setNewVehicleTemporaryFiles(prev => [...prev, { fileType, files: filesArr }]);

          // Set files to display
          setVehicleFilesToDisplay(prev => {
            if (fileType === FILE_TYPE_OTHER) {
              // We can have multiple other files
              return [
                ...prev,
                ...filesArr.map((file: File) => ({
                  id: `${fileType}-${file.name}`,
                  type: fileType,
                  file: URL.createObjectURL(file),
                  name: file.name,
                })),
              ];
            }

            // Replace file as we can have only one of this type
            return [
              ...prev,
              {
                id: `${fileType}-${filesArr[0].name}`,
                type: fileType,
                file: URL.createObjectURL(filesArr[0]),
                name: filesArr[0].name,
              },
            ];
          });
        }
      }
    };

    const uploadPOAFileRequest = async (importerId: string, file: File) => {
      const formData = new FormData();
      formData.append('poa', file);

      await axiosService.patch(`/entities/importer/${importerId}/`, formData);
    };

    const onPOAFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!importerChoice) {
        // No importer selected, return
        e.target.value = '';
        return;
      }

      const { files } = e.target;
      if (files && files.length > 0) {
        // Cast the files to an array
        const filesArr = [...files];

        // Clear input value in order to be able to upload again
        e.target.value = '';

        // Set POA file on the selected Importer instance
        setIsLoading?.(true);
        uploadPOAFileRequest(importerChoice.id, filesArr[0])
          .then(() => {
            if (onPOAUploaded) {
              onPOAUploaded();
            }
          })
          .catch((err: ErrorResponse) => onError?.(err))
          .finally(() => setIsLoading?.(false));
      }
    };

    const deleteVehicleFile = (fileId: string) => {
      if (addingNewVehicle) {
        // Remove file from temporary files
        setNewVehicleTemporaryFiles(prev => {
          const filteredNewVehicleTemporaryFiles = [...prev];
          filteredNewVehicleTemporaryFiles.forEach(temporaryFile =>
            temporaryFile.files.filter(file => `${temporaryFile.fileType}-${file.name}` !== fileId)
          );
          return filteredNewVehicleTemporaryFiles.filter(temporaryFile => temporaryFile.files.length === 0);
        });

        // Remove file from the displayed files
        setVehicleFilesToDisplay(prev => prev.filter(file => file.id !== fileId));
      } else if (vehicleId) {
        // Make a POST request to delete the file
        setIsLoading?.(true);
        axiosService
          .post(`/vehicles/vehicle/${vehicleId}/delete_file/`, { id: fileId })
          .then(() => setVehicleFilesToDisplay(prev => prev.filter(file => file.id !== fileId)))
          .catch(err => onError?.(err))
          .finally(() => setIsLoading?.(false));
      }
    };

    const deletePOAFile = () => {
      // Make a POST request to remove the POA file
      axiosService
        .patch(`/entities/importer/${importerChoice?.id}/`, { poa: null })
        .then(() => {
          if (onPOAUploaded) {
            onPOAUploaded();
          }
        })
        .catch((err: ErrorResponse) => onError?.(err));
    };

    const deleteFileButton = (fileType: string, fileId: string) => (
      <Tooltip title="Delete">
        <IconButton
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setFileToDelete({ fileType, fileId });
          }}
          size="small"
          sx={{ padding: '3px' }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    );

    const viewFileButton = (fileSrc: string) => (
      <Button
        component="label"
        variant="contained"
        size="small"
        onClick={() => window.open(fileSrc, '_blank', 'noreferrer')}
        sx={{ width: '80px' }}
      >
        View
      </Button>
    );

    const uploadFileButton = (fileType: string) => (
      <Button
        disabled={fileType === FILE_TYPE_POA && !importerChoice}
        component="label"
        variant="contained"
        size="small"
        sx={{ width: '80px' }}
      >
        Upload
        <input
          type="file"
          accept="image/png, image/jpg, image/jpeg, application/pdf"
          hidden
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (fileType === FILE_TYPE_POA) {
              // Attach the POA to the selected importer's instance
              onPOAFileInput(e);
            } else {
              onVehicleFileInput(fileType, e);
            }
          }}
        />
      </Button>
    );

    const getFileComponentByType = (fileType: string) => {
      if (fileType === FILE_TYPE_OTHER) {
        const filesTypesOther = vehicleFilesToDisplay.filter(file => file.type === FILE_TYPE_OTHER);
        if (filesTypesOther.length > 0) {
          return filesTypesOther.map((file: VehicleFile) => {
            const fileName = addingNewVehicle ? file.name : documentDisplayName(file.file);
            return (
              <Box key={file.id} sx={vehiclePageStyles.detailBox}>
                <Tooltip title={fileName}>
                  <Typography sx={{ ...vehiclePageStyles.detailTitle, ...vehiclePageStyles.fileName }}>
                    {fileName}
                  </Typography>
                </Tooltip>
                <Box sx={{ ...vehiclePageStyles.detailValue, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {deleteFileButton(fileType, file.id)}
                  {viewFileButton(file.file)}
                </Box>
              </Box>
            );
          });
        }

        // No other files
        return <Typography sx={{ fontStyle: 'normal' }}>No other files.</Typography>;
      }

      if (fileType === FILE_TYPE_POA) {
        const file = getPOAFile?.();
        return (
          <Box sx={{ ...vehiclePageStyles.detailValue, display: 'flex', alignItems: 'center', gap: '10px' }}>
            {file ? (
              <>
                {deleteFileButton(fileType, '')}
                <CheckCircle sx={{ width: 20, height: 20 }} color="success" />
                {viewFileButton(`${process.env.REACT_APP_MEDIA_API_URL}/${file}`)}
              </>
            ) : (
              <>
                <Cancel sx={{ width: 20, height: 20 }} color="error" />
                {uploadFileButton(fileType)}
              </>
            )}
          </Box>
        );
      }

      // Vehicle files of specific type such as gate_pass, bill_of_sale, invoice, title, etc.
      const file = vehicleFilesToDisplay.find(file => file.type === fileType);
      return (
        <Box sx={{ ...vehiclePageStyles.detailValue, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {file ? (
            <>
              {deleteFileButton(fileType, file.id)}
              <CheckCircle sx={{ width: 20, height: 20 }} color="success" />
              {viewFileButton(file.file)}
            </>
          ) : (
            <>
              <Cancel sx={{ width: 20, height: 20 }} color="error" />
              {uploadFileButton(fileType)}
            </>
          )}
        </Box>
      );
    };

    // Initialize files on mount
    React.useEffect(() => {
      if (initialFiles.length > 0) {
        setVehicleFilesToDisplay(parseVehicleFiles(initialFiles));
      }
    }, [initialFiles, parseVehicleFiles]);

    // Update submit data when title status changes
    React.useEffect(() => {
      updateSubmitData({
        // eslint-disable-next-line camelcase
        title_status: titleStatusInput,
      });
    }, [titleStatusInput, updateSubmitData]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      uploadNewVehicleFiles: async (newVehicleId: string) => {
        if (newVehicleTemporaryFiles.length > 0) {
          const uploadPromises = newVehicleTemporaryFiles.map(temporaryFile =>
            uploadVehicleFiles(newVehicleId, temporaryFile.fileType, temporaryFile.files)
          );
          await Promise.allSettled(uploadPromises);
        }
      },
      getTemporaryFiles: () => newVehicleTemporaryFiles,
      updateSectionData: (vehicleData: { title_status?: string }) => {
        setTitleStatusInput(vehicleData?.title_status || '');
      },
    }));

    return (
      <>
        <Box sx={vehiclePageStyles.card}>
          <Box sx={vehiclePageStyles.cardHeader}>Documents</Box>
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Gate Pass</Typography>
            {getFileComponentByType(FILE_TYPE_GATE_PASS)}
          </Box>
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Bill of sale</Typography>
            {getFileComponentByType(FILE_TYPE_BILL_OF_SALE)}
          </Box>
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>POA</Typography>
            {getFileComponentByType(FILE_TYPE_POA)}
          </Box>
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Invoice</Typography>
            {getFileComponentByType(FILE_TYPE_INVOICE)}
          </Box>
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Title</Typography>
            {getFileComponentByType(FILE_TYPE_TITLE)}
          </Box>
          {(isSuperuser === true || (isSuperuser === false && vehicleId !== undefined)) && (
            <Box sx={vehiclePageStyles.detailBox}>
              <Typography sx={vehiclePageStyles.detailTitle}>Title Status</Typography>
              {canEdit(VEHICLE_FIELDS.TITLE_STATUS) ? (
                <TextField
                  variant="standard"
                  size="small"
                  id="title-status"
                  value={titleStatusInput}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setTitleStatusInput(event.target.value);
                  }}
                  sx={vehiclePageStyles.detailTextField}
                  inputProps={{
                    sx: {
                      ...vehiclePageStyles.detailTextFieldInput,
                    },
                  }}
                />
              ) : (
                <Box sx={vehiclePageStyles.detailValue}>{titleStatusInput || '-'}</Box>
              )}
            </Box>
          )}
          <Box sx={vehiclePageStyles.detailsSeparator}></Box>
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Other Files</Typography>
            {uploadFileButton(FILE_TYPE_OTHER)}
          </Box>
          {getFileComponentByType(FILE_TYPE_OTHER)}
        </Box>

        {/* Delete File Dialog */}
        <Dialog open={!!fileToDelete} onClose={() => setFileToDelete(null)} style={{ zIndex: '10000' }}>
          <DialogTitle id="alert-dialog-title">
            {fileToDelete?.fileType === FILE_TYPE_POA ? 'Delete POA' : 'Delete file'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {fileToDelete?.fileType === FILE_TYPE_POA
                ? `Are you sure you want to delete the currently selected Importer's (${importerChoice?.name}) POA file?`
                : 'Are you sure you want to delete this file?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFileToDelete(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (fileToDelete) {
                  if (fileToDelete.fileType === FILE_TYPE_POA) {
                    deletePOAFile();
                  } else if (fileToDelete) {
                    deleteVehicleFile(fileToDelete.fileId);
                  }
                }
                setFileToDelete(null);
              }}
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

VehicleDocumentsSection.displayName = 'VehicleDocumentsSection';

export default VehicleDocumentsSection;
